// A7 — THE SIMULATION SWITCHER. Brief 65 (brief 50 §4), the sixth of the sixteen.
//
// Swapping the JWT at an auth endpoint and reloading the app. It was
// `startSimulation` and `endSimulation`, and the census pairs them at 70.5% —
// the lowest ratio in band A, which is exactly why §2 is the section that
// decides it:
//
//   src/App.tsx:1640-1670@8fdbbd6c x src/App.tsx:1671-1700@8fdbbd6c  70.5%
//
// SIX DIFFERENCES OVER THIRTY LINES, and every one of them is a parameter or
// stays at its call site. None became a choice:
//
//   the endpoint        -> `url`
//   the body            -> `body`, and OMITTED rather than passed as
//                          `undefined`: `end-simulate` sends no body at all,
//                          and this builds the init object without the member
//                          rather than resting on `body: undefined` being
//                          treated as absent
//   what to do with the -> `onSwapped(j)`. Start writes the id it asked for;
//   answer                 end writes the one the server hands back, and only
//                          if it hands one back
//   the failure           -> `onError(e)`. The two toasts say different things
//                          and end also signs the user out after 1.5s, so the
//                          whole catch belongs to the caller
//   the empty-arg guard -> stays at the call site (`if (!targetId) return`)
//   the M+3.7.2 note    -> stays with the caller it is about
//
// WHAT IS SHARED IS THE SWAP ITSELF: POST under WHICHEVER CREDENTIALS THE
// BROWSER HOLDS (174 §1.2 — the lx:token Bearer if a pre-167 session left one,
// the lx_session cookie, and the X-LexCRM the cookie path needs), read the
// JSON, throw on a non-ok, tell the caller, reload — the server set the new
// session cookie on the response, so nothing is stored.
// `window.location.reload()` stays INSIDE the try, where it was — so an
// `onSwapped` that throws still reaches the caller's error path, exactly as
// `sv(SK.cu, …)` did.

import { authHeaders } from "../api";

export async function swapSimulationToken(opts: {
  url: string;
  body?: unknown;
  onSwapped: (j: any) => void;
  onError: (e: any) => void;
}) {
  try {
    /* 174 §1.2 — BOTH credentials, and this one is not optional: the swap
       REPLACES the session, and `/auth/end-simulate` reads the credential
       itself rather than going through authMiddleware (auth.js:1142). Sent
       cookie-only, a cookie-less admin could neither start nor end a
       simulation at all. The bare fetch stays — api()'s 401 branch wipes
       storage and lands on the login screen, which is the wrong answer for a
       swap that already owns its own failure path — and it spreads exactly
       what api() spreads, including the X-LexCRM this POST needs under cookie
       auth. */
    const r = await fetch(opts.url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
      },
      ...(opts.body === undefined ? {} : { body: JSON.stringify(opts.body) }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error || "HTTP " + r.status);
    // 167 §2d — the swap's response set the cookie (sendSession); this stores
    // no token. onSwapped writes the non-secret lx:sim banner marker.
    opts.onSwapped(j);
    window.location.reload();
  } catch (e) {
    opts.onError(e);
  }
}
